import { openDB } from 'idb';
import { getAccountIdFromLocation } from './browserNavigation';
import {
  getLocalStorage,
  safeGetJsonStorageItem,
  safeSetStorageItem,
} from './browserStorage';

/*
  In-memory cache for prefetched conversation messages.

  Security boundary:
  - conversation IDs are display IDs and are only unique inside an account;
  - every cache and in-flight key is therefore scoped by accountId + conversationId;
  - legacy callers may omit accountId while they are on an account-scoped route;
    new asynchronous flows should pass an explicit accountId captured before the
    request starts, so a later route change cannot re-scope its response.

  Memory is the synchronous fast path. Persistence is explicit through `prime`
  and `restore`, stored in a dedicated account database that is tracked for the
  existing logout cleanup flow.
*/

const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const MAX_ENTRIES = 100;
const DEFAULT_IN_FLIGHT_REQUEST_KEY = 'messages';
const PERSISTENT_DB_PREFIX = 'otello-message-cache-';
const PERSISTENT_DB_VERSION = 1;
const PERSISTENT_STORE_NAME = 'messages';
const TRACKED_IDB_NAMES_KEY = 'otello-idb-names';

const byConversation = new Map();
const byLastMessage = new Map();
const inFlightRequests = new Map();
const pageSignatures = new Map();
const persistentDatabases = new Map();
const pendingPersistenceOperations = new Set();
const accountInvalidationEpochs = new Map();
const conversationInvalidationEpochs = new Map();
let globalInvalidationEpoch = 0;

const now = () => Date.now();

const normalizeId = (id) => {
  if (id === null || id === undefined) return null;

  const value = String(id).trim();
  if (!value) return null;

  const numericId = Number(value);
  return Number.isFinite(numericId) ? numericId : value;
};

const resolveAccountId = (accountId = undefined) =>
  normalizeId(accountId === undefined ? getAccountIdFromLocation() : accountId);

const buildConversationKey = (accountId, conversationId) =>
  JSON.stringify([accountId, conversationId]);

const buildLastMessageKey = (accountId, conversationId, lastMessageId) =>
  JSON.stringify([accountId, conversationId, lastMessageId]);

const buildInFlightKey = (
  accountId,
  conversationId,
  requestKey = DEFAULT_IN_FLIGHT_REQUEST_KEY
) => JSON.stringify([accountId, conversationId, String(requestKey)]);

const buildPersistentDbName = (accountId) =>
  `${PERSISTENT_DB_PREFIX}${accountId}`;

const getInvalidationEpoch = (epochs, key) => epochs.get(key) || 0;

const captureInvalidationTokenForScope = (scope) => ({
  accountEpoch: getInvalidationEpoch(
    accountInvalidationEpochs,
    scope.accountId
  ),
  conversationEpoch: getInvalidationEpoch(
    conversationInvalidationEpochs,
    buildConversationKey(scope.accountId, scope.conversationId)
  ),
  globalEpoch: globalInvalidationEpoch,
});

const isInvalidationTokenCurrentForScope = (scope, token = null) =>
  Boolean(
    token &&
    token.globalEpoch === globalInvalidationEpoch &&
    token.accountEpoch ===
      getInvalidationEpoch(accountInvalidationEpochs, scope.accountId) &&
    token.conversationEpoch ===
      getInvalidationEpoch(
        conversationInvalidationEpochs,
        buildConversationKey(scope.accountId, scope.conversationId)
      )
  );

const bumpConversationInvalidationEpoch = (scope) => {
  const key = buildConversationKey(scope.accountId, scope.conversationId);
  conversationInvalidationEpochs.set(
    key,
    getInvalidationEpoch(conversationInvalidationEpochs, key) + 1
  );
};

const bumpAccountInvalidationEpoch = (accountId) => {
  accountInvalidationEpochs.set(
    accountId,
    getInvalidationEpoch(accountInvalidationEpochs, accountId) + 1
  );
};

const trackPersistentDbName = (dbName) => {
  const storage = getLocalStorage();
  const names = safeGetJsonStorageItem(storage, TRACKED_IDB_NAMES_KEY, []);
  const trackedNames = Array.isArray(names) ? names : [];
  if (trackedNames.includes(dbName)) return;

  safeSetStorageItem(
    storage,
    TRACKED_IDB_NAMES_KEY,
    JSON.stringify([...trackedNames, dbName])
  );
};

const openPersistentDatabase = (accountId) => {
  if (persistentDatabases.has(accountId)) {
    return persistentDatabases.get(accountId);
  }

  const dbName = buildPersistentDbName(accountId);
  const dbPromise = Promise.resolve()
    .then(() =>
      openDB(dbName, PERSISTENT_DB_VERSION, {
        blocking(_currentVersion, _blockedVersion, event) {
          // Logout deletes every DB tracked in `otello-idb-names`. Release this
          // connection immediately so deleteDatabase cannot remain blocked.
          event?.target?.close?.();
        },
        upgrade(db) {
          if (!db.objectStoreNames?.contains?.(PERSISTENT_STORE_NAME)) {
            db.createObjectStore(PERSISTENT_STORE_NAME, {
              keyPath: 'conversationId',
            });
          }
        },
      })
    )
    .then((db) => {
      if (!db) return null;
      trackPersistentDbName(dbName);
      return db;
    })
    .catch(() => null);

  persistentDatabases.set(accountId, dbPromise);
  return dbPromise;
};

const trackPersistenceOperation = (operation) => {
  const trackedOperation = Promise.resolve(operation).catch(() => false);
  pendingPersistenceOperations.add(trackedOperation);
  const cleanup = () => pendingPersistenceOperations.delete(trackedOperation);
  trackedOperation.then(cleanup, cleanup);
  return trackedOperation;
};

const resolveScope = (scopeOrAccountId, explicitConversationId = undefined) => {
  const isObjectScope =
    scopeOrAccountId &&
    typeof scopeOrAccountId === 'object' &&
    !Array.isArray(scopeOrAccountId);

  let rawAccountId;
  let rawConversationId;
  if (isObjectScope) {
    rawAccountId = scopeOrAccountId.accountId;
    rawConversationId = scopeOrAccountId.conversationId;
  } else if (explicitConversationId === undefined) {
    rawAccountId = undefined;
    rawConversationId = scopeOrAccountId;
  } else {
    rawAccountId = scopeOrAccountId;
    rawConversationId = explicitConversationId;
  }

  const accountId = resolveAccountId(rawAccountId);
  const conversationId = normalizeId(rawConversationId);

  if (accountId === null || conversationId === null) return null;
  return { accountId, conversationId };
};

const hasLastMessageId = (lastMessageId) =>
  lastMessageId !== null &&
  lastMessageId !== undefined &&
  String(lastMessageId).trim() !== '';

const isExpired = (entry) => !entry || entry.expiresAt <= now();

const removeEntry = (entry) => {
  if (!entry) return false;

  if (byConversation.get(entry.conversationKey) === entry) {
    byConversation.delete(entry.conversationKey);
  }
  if (
    entry.lastMessageKey &&
    byLastMessage.get(entry.lastMessageKey) === entry
  ) {
    byLastMessage.delete(entry.lastMessageKey);
  }
  return true;
};

const touchEntry = (entry) => {
  if (!entry || byConversation.get(entry.conversationKey) !== entry) return;
  byConversation.delete(entry.conversationKey);
  byConversation.set(entry.conversationKey, entry);
};

const readEntry = (entry) => {
  if (isExpired(entry)) {
    removeEntry(entry);
    return null;
  }

  touchEntry(entry);
  return entry.data;
};

const evictIfNeeded = () => {
  while (byConversation.size > MAX_ENTRIES) {
    const oldestKey = byConversation.keys().next().value;
    removeEntry(byConversation.get(oldestKey));
  }
};

const parseSetArguments = (
  scopeOrConversationId,
  lastMessageId,
  data,
  ttlMs
) => {
  const isObjectScope =
    scopeOrConversationId &&
    typeof scopeOrConversationId === 'object' &&
    !Array.isArray(scopeOrConversationId);

  if (isObjectScope) {
    return {
      data: scopeOrConversationId.data,
      invalidationToken: scopeOrConversationId.invalidationToken,
      lastMessageId: scopeOrConversationId.lastMessageId,
      scope: resolveScope(scopeOrConversationId),
      ttlMs: scopeOrConversationId.ttlMs,
    };
  }

  return {
    data,
    invalidationToken: null,
    lastMessageId,
    scope: resolveScope(scopeOrConversationId),
    ttlMs,
  };
};

const parseLookupArguments = (scopeOrConversationId, lastMessageId = null) => {
  const isObjectScope =
    scopeOrConversationId &&
    typeof scopeOrConversationId === 'object' &&
    !Array.isArray(scopeOrConversationId);

  return {
    lastMessageId: isObjectScope
      ? scopeOrConversationId.lastMessageId
      : lastMessageId,
    scope: resolveScope(scopeOrConversationId),
  };
};

const parseInFlightArguments = (
  scopeOrAccountId,
  conversationOrFactory,
  factoryOrRequestKey,
  explicitRequestKey
) => {
  const isObjectScope =
    scopeOrAccountId &&
    typeof scopeOrAccountId === 'object' &&
    !Array.isArray(scopeOrAccountId);

  if (isObjectScope) {
    return {
      factory: conversationOrFactory,
      requestKey:
        scopeOrAccountId.requestKey ??
        factoryOrRequestKey ??
        DEFAULT_IN_FLIGHT_REQUEST_KEY,
      scope: resolveScope(scopeOrAccountId),
    };
  }

  if (typeof conversationOrFactory === 'function') {
    return {
      factory: conversationOrFactory,
      requestKey: factoryOrRequestKey ?? DEFAULT_IN_FLIGHT_REQUEST_KEY,
      scope: resolveScope(scopeOrAccountId),
    };
  }

  return {
    factory: factoryOrRequestKey,
    requestKey: explicitRequestKey ?? DEFAULT_IN_FLIGHT_REQUEST_KEY,
    scope: resolveScope(scopeOrAccountId, conversationOrFactory),
  };
};

const runFactory = (factory) => {
  try {
    return Promise.resolve(factory());
  } catch (error) {
    return Promise.reject(error);
  }
};

const getOrCreateInFlight = (...args) => {
  const { factory, requestKey, scope } = parseInFlightArguments(...args);
  if (typeof factory !== 'function') {
    return Promise.reject(
      new TypeError('An in-flight request factory is required')
    );
  }

  // Outside an account-scoped context we preserve request behavior but do not
  // share it under an unsafe global key.
  if (!scope) return runFactory(factory);

  const key = buildInFlightKey(
    scope.accountId,
    scope.conversationId,
    requestKey
  );
  const current = inFlightRequests.get(key);
  if (current) return current.promise;

  const promise = runFactory(factory);
  const record = { ...scope, key, promise };
  inFlightRequests.set(key, record);

  const cleanup = () => {
    if (inFlightRequests.get(key) === record) {
      inFlightRequests.delete(key);
    }
  };
  promise.then(cleanup, cleanup);
  return promise;
};

const toPersistentRecord = (entry) => ({
  conversationId: entry.conversationId,
  data: entry.data,
  expiresAt: entry.expiresAt,
  lastMessageId: entry.lastMessageId,
});

const persistEntry = async (entry) => {
  if (!entry) return false;

  const db = await openPersistentDatabase(entry.accountId);
  if (!db) return false;

  try {
    await db.put(PERSISTENT_STORE_NAME, toPersistentRecord(entry));
    return true;
  } catch (_) {
    return false;
  }
};

const readPersistentEntry = async ({ accountId, conversationId }) => {
  const db = await openPersistentDatabase(accountId);
  if (!db) return null;

  try {
    return (await db.get(PERSISTENT_STORE_NAME, conversationId)) || null;
  } catch (_) {
    return null;
  }
};

const deletePersistentConversation = async ({ accountId, conversationId }) => {
  const db = await openPersistentDatabase(accountId);
  if (!db) return false;

  try {
    await db.delete(PERSISTENT_STORE_NAME, conversationId);
    return true;
  } catch (_) {
    return false;
  }
};

const clearPersistentAccount = async (accountId) => {
  const db = await openPersistentDatabase(accountId);
  if (!db) return false;

  try {
    await db.clear(PERSISTENT_STORE_NAME);
    return true;
  } catch (_) {
    return false;
  }
};

const waitForPendingPersistence = async () => {
  if (!pendingPersistenceOperations.size) return;
  await Promise.all(Array.from(pendingPersistenceOperations));
};

export const messagePrefetchCache = {
  set(scopeOrConversationId, lastMessageId, data, ttlMs = DEFAULT_TTL_MS) {
    const parsed = parseSetArguments(
      scopeOrConversationId,
      lastMessageId,
      data,
      ttlMs
    );
    if (!parsed.scope || parsed.data === null || parsed.data === undefined) {
      return false;
    }
    if (
      parsed.invalidationToken &&
      !isInvalidationTokenCurrentForScope(
        parsed.scope,
        parsed.invalidationToken
      )
    ) {
      return false;
    }

    const { accountId, conversationId } = parsed.scope;
    const normalizedLastMessageId = hasLastMessageId(parsed.lastMessageId)
      ? normalizeId(parsed.lastMessageId)
      : null;
    const conversationKey = buildConversationKey(accountId, conversationId);
    removeEntry(byConversation.get(conversationKey));

    const numericTtl = Number(parsed.ttlMs);
    const effectiveTtl =
      Number.isFinite(numericTtl) && numericTtl !== 0
        ? numericTtl
        : DEFAULT_TTL_MS;
    const lastMessageKey =
      normalizedLastMessageId === null
        ? null
        : buildLastMessageKey(
            accountId,
            conversationId,
            normalizedLastMessageId
          );
    const entry = {
      accountId,
      conversationId,
      conversationKey,
      data: parsed.data,
      expiresAt: now() + effectiveTtl,
      lastMessageId: normalizedLastMessageId,
      lastMessageKey,
    };

    byConversation.set(conversationKey, entry);
    if (lastMessageKey) byLastMessage.set(lastMessageKey, entry);
    evictIfNeeded();
    return true;
  },

  get(scopeOrConversationId, lastMessageId = null) {
    const parsed = parseLookupArguments(scopeOrConversationId, lastMessageId);
    if (!parsed.scope) return null;

    const { accountId, conversationId } = parsed.scope;
    if (hasLastMessageId(parsed.lastMessageId)) {
      const normalizedLastMessageId = normalizeId(parsed.lastMessageId);
      return readEntry(
        byLastMessage.get(
          buildLastMessageKey(
            accountId,
            conversationId,
            normalizedLastMessageId
          )
        )
      );
    }

    return readEntry(
      byConversation.get(buildConversationKey(accountId, conversationId))
    );
  },

  hasValid(scopeOrConversationId, lastMessageId = null) {
    return Boolean(this.get(scopeOrConversationId, lastMessageId));
  },

  getOrCreateInFlight,

  // Alias that reads naturally at call sites which only need request dedupe.
  dedupeInFlight: getOrCreateInFlight,

  captureInvalidationToken(scopeOrAccountId, explicitConversationId) {
    const scope = resolveScope(scopeOrAccountId, explicitConversationId);
    return scope ? captureInvalidationTokenForScope(scope) : null;
  },

  isInvalidationTokenCurrent(scopeOrAccountId, token, explicitConversationId) {
    const scope = resolveScope(scopeOrAccountId, explicitConversationId);
    return scope ? isInvalidationTokenCurrentForScope(scope, token) : false;
  },

  // Writes memory first and persists best-effort. `false` means callers still
  // have a valid memory entry but IndexedDB was unavailable or rejected it.
  async prime(
    scopeOrConversationId,
    lastMessageId,
    data,
    ttlMs = DEFAULT_TTL_MS
  ) {
    const parsed = parseSetArguments(
      scopeOrConversationId,
      lastMessageId,
      data,
      ttlMs
    );
    if (
      !this.set(scopeOrConversationId, lastMessageId, data, ttlMs) ||
      !parsed.scope
    ) {
      return false;
    }

    await waitForPendingPersistence();
    if (
      parsed.invalidationToken &&
      !isInvalidationTokenCurrentForScope(
        parsed.scope,
        parsed.invalidationToken
      )
    ) {
      return false;
    }
    const entry = byConversation.get(
      buildConversationKey(parsed.scope.accountId, parsed.scope.conversationId)
    );
    return persistEntry(entry);
  },

  // Hydrates a single account-scoped entry lazily. Concurrent restores share
  // one Promise and expired records are deleted before returning a miss.
  restore(scopeOrConversationId, lastMessageId = null) {
    const parsed = parseLookupArguments(scopeOrConversationId, lastMessageId);
    if (!parsed.scope) return Promise.resolve(null);

    const memoryValue = this.get(scopeOrConversationId, lastMessageId);
    if (memoryValue) return Promise.resolve(memoryValue);

    const requestedLastMessageId = hasLastMessageId(parsed.lastMessageId)
      ? normalizeId(parsed.lastMessageId)
      : null;
    const invalidationToken = captureInvalidationTokenForScope(parsed.scope);
    const requestKey = `restore:${requestedLastMessageId ?? '*'}`;
    return getOrCreateInFlight({ ...parsed.scope, requestKey }, async () => {
      await waitForPendingPersistence();
      if (
        !isInvalidationTokenCurrentForScope(parsed.scope, invalidationToken)
      ) {
        return null;
      }
      const persisted = await readPersistentEntry(parsed.scope);
      if (
        !isInvalidationTokenCurrentForScope(parsed.scope, invalidationToken)
      ) {
        return null;
      }
      if (!persisted) return null;

      const expiresAt = Number(persisted.expiresAt);
      if (!Number.isFinite(expiresAt) || expiresAt <= now()) {
        await deletePersistentConversation(parsed.scope);
        return null;
      }

      const persistedLastMessageId = hasLastMessageId(persisted.lastMessageId)
        ? normalizeId(persisted.lastMessageId)
        : null;
      if (
        requestedLastMessageId !== null &&
        persistedLastMessageId !== requestedLastMessageId
      ) {
        return null;
      }
      if (persisted.data === null || persisted.data === undefined) {
        await deletePersistentConversation(parsed.scope);
        return null;
      }

      this.set({
        ...parsed.scope,
        data: persisted.data,
        invalidationToken,
        lastMessageId: persistedLastMessageId,
        ttlMs: expiresAt - now(),
      });
      return persisted.data;
    });
  },

  invalidateConversation(scopeOrAccountId, explicitConversationId = undefined) {
    const scope = resolveScope(scopeOrAccountId, explicitConversationId);
    if (!scope) return false;

    bumpConversationInvalidationEpoch(scope);

    const conversationKey = buildConversationKey(
      scope.accountId,
      scope.conversationId
    );
    let changed = removeEntry(byConversation.get(conversationKey));

    inFlightRequests.forEach((record, key) => {
      if (
        record.accountId === scope.accountId &&
        record.conversationId === scope.conversationId
      ) {
        inFlightRequests.delete(key);
        changed = true;
      }
    });
    trackPersistenceOperation(deletePersistentConversation(scope));
    return changed;
  },

  invalidateAccount(accountId = undefined) {
    const normalizedAccountId = resolveAccountId(accountId);
    if (normalizedAccountId === null) return false;

    bumpAccountInvalidationEpoch(normalizedAccountId);

    let changed = false;
    Array.from(byConversation.values()).forEach((entry) => {
      if (entry.accountId === normalizedAccountId) {
        removeEntry(entry);
        changed = true;
      }
    });
    inFlightRequests.forEach((record, key) => {
      if (record.accountId === normalizedAccountId) {
        inFlightRequests.delete(key);
        changed = true;
      }
    });
    if (pageSignatures.delete(normalizedAccountId)) changed = true;
    trackPersistenceOperation(clearPersistentAccount(normalizedAccountId));
    return changed;
  },

  clearAll() {
    // Synchronous compatibility API: persistent data is cleared by the scoped
    // invalidators above or by the tracked IndexedDB logout cleanup.
    byConversation.clear();
    byLastMessage.clear();
    inFlightRequests.clear();
    pageSignatures.clear();
    globalInvalidationEpoch += 1;
    accountInvalidationEpochs.clear();
    conversationInvalidationEpochs.clear();
  },

  async flushPersistence() {
    await waitForPendingPersistence();
  },

  setPageSignature(signatureOrOptions, explicitAccountId = undefined) {
    const isObjectScope =
      signatureOrOptions &&
      typeof signatureOrOptions === 'object' &&
      !Array.isArray(signatureOrOptions);
    const accountId = resolveAccountId(
      isObjectScope ? signatureOrOptions.accountId : explicitAccountId
    );
    if (accountId === null) return false;

    const signature = isObjectScope
      ? signatureOrOptions.signature
      : signatureOrOptions;
    if (signature) {
      pageSignatures.set(accountId, signature);
    } else {
      pageSignatures.delete(accountId);
    }
    return true;
  },

  isSameSignature(signatureOrOptions, explicitAccountId = undefined) {
    const isObjectScope =
      signatureOrOptions &&
      typeof signatureOrOptions === 'object' &&
      !Array.isArray(signatureOrOptions);
    const accountId = resolveAccountId(
      isObjectScope ? signatureOrOptions.accountId : explicitAccountId
    );
    if (accountId === null) return false;

    const signature = isObjectScope
      ? signatureOrOptions.signature
      : signatureOrOptions;
    return Boolean(signature && pageSignatures.get(accountId) === signature);
  },
};

export default messagePrefetchCache;
